import { useRef, useState, useCallback } from 'react';
import qs from 'qs';
import { IStoryMargins } from '@opengov/stories-shared/types';
import { getJson } from '../../../../utils/fetchUtils';

// API response type for /api/stories/v1/stories/:id/pdf
type IPreviewPdfResponse = {
  pdf?: string;
  message?: string;
  err?: Error | string;
};

export type PreviewStoryPdfParams = {
  storyId: string;
  entitySubdomain: string;
  displayDate?: boolean;
  displayPageNumber?: boolean;
  displayTitle?: boolean;
  enlargeFonts?: boolean;
  isTemplate?: boolean;
  storyMarginsEnabled?: boolean;
  margins?: IStoryMargins | null;
  startingPage?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  watermark?: string | null;
};

export type PreviewStoryPdfResult = {
  pdfUrl: string;
};

export type UsePreviewStoryPdfOptions = {
  onSuccess?: (result: PreviewStoryPdfResult) => void;
};

/**
 * Hook for previewing story PDF
 * Follows the same pattern as PublicationSetupPDFView - fire-and-forget with no error handling
 */
export function usePreviewStoryPdf({ onSuccess }: UsePreviewStoryPdfOptions = {}) {
  const controllerRef = useRef<AbortController | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const generatePreview = useCallback(
    ({
      storyId,
      entitySubdomain,
      displayDate = false,
      displayPageNumber = true,
      displayTitle = true,
      enlargeFonts = false,
      isTemplate = false,
      storyMarginsEnabled = false,
      margins = null,
      startingPage = '1',
      showHeader = true,
      showFooter = true,
      watermark = null
    }: PreviewStoryPdfParams) => {
      // Abort any previous request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Create new controller for this request
      const controller = new AbortController();
      controllerRef.current = controller;
      setIsLoading(true);

      const query = qs.stringify(
        {
          displayPageNumber,
          displayTitle,
          displayDate,
          enlargeFonts,
          template: isTemplate,
          showMargins: !storyMarginsEnabled,
          entitySubdomain,
          startingPage,
          margins: margins ? encodeURIComponent(JSON.stringify(margins)) : undefined,
          showHeader,
          showFooter,
          isExportFromSetupPdf: true,
          watermark
        },
        { encode: false }
      );

      // Fire-and-forget pattern - only handle success, ignore errors
      void getJson<IPreviewPdfResponse>(`/api/stories/v1/stories/${storyId}/pdf?${query}`, controller.signal)
        .then((response) => {
          if (response?.pdf) {
            const pdfUrl = `${response.pdf}?v=${Date.now()}`;
            setPdfUrl(pdfUrl);
            onSuccess?.({ pdfUrl: pdfUrl });
          }
        })
        // Swallow errors (incl. AbortError from a superseded request) — fire-and-forget.
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [onSuccess]
  );

  // Cancel current request and reset state
  const cancelPreview = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setIsLoading(false);
    setPdfUrl(null);
  }, []);

  // Reset all state
  const reset = useCallback(() => {
    cancelPreview();
  }, [cancelPreview]);

  return {
    mutate: generatePreview,
    pdfUrl,
    isLoading,
    cancelPreview,
    reset
  };
}
